import { useCallback, useEffect, useRef, useState } from "react";
import { HEARTBEAT_INTERVAL_MS } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { indexPresenceByKey } from "../util/scribePresence";

// #2365 — poll cadence matches score-common's HEARTBEAT_INTERVAL_MS (the
// same constant scribe-fe's sender uses, #2364): scoreadmin sees a fresh
// heartbeat about as often as one is actually sent, without polling faster
// than the data can change. Visibility-aware, same shape as
// ShowDashboard.jsx's own poll (#823): paused while the tab is hidden,
// refreshed immediately the moment it's visible again.
const PRESENCE_POLL_MS = HEARTBEAT_INTERVAL_MS;

export function usePresence(showId) {
  const scoreApi = useScoreApi();
  const [observedByKey, setObservedByKey] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const scoreApiRef = useRef(scoreApi);
  scoreApiRef.current = scoreApi;
  const showIdRef = useRef(showId);
  showIdRef.current = showId;

  // A new show means a fresh snapshot — carrying rows across a show switch
  // would misattribute one show's silence to another's seats.
  useEffect(() => {
    setObservedByKey({});
  }, [showId]);

  const refresh = useCallback(async () => {
    const id = showIdRef.current;
    if (!id) return;
    try {
      const list = unwrap(await scoreApiRef.current.presence.listPresence(id));
      // #2370: each poll REPLACES the snapshot. The server returns stale rows
      // tagged live:false inside its bounded window, so no client-side memory
      // of earlier polls is needed (or wanted: it would not survive a reload).
      setObservedByKey(indexPresenceByKey(list || []));
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!showId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    if (document.visibilityState !== "hidden") refresh();

    let timer = null;
    const start = () => {
      if (document.visibilityState === "hidden") return;
      if (timer == null) timer = setInterval(refresh, PRESENCE_POLL_MS);
    };
    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") stop();
      else {
        refresh();
        start();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [showId, refresh]);

  return { observedByKey, loading, error, refetch: refresh };
}

export default usePresence;
